import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Warning, ArrowsClockwise, CheckCircle, MagnifyingGlass, Info } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StockAudit() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/admin/stock-audit`, { withCredentials: true });
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo cargar la auditoría');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="stock-audit-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">🔍 Diagnóstico de Stock</h1>
          <p className="text-sm text-slate-500 mt-1">
            Compara el stock actual con los movimientos + compras registradas por sucursal. Solo diagnostica; no ajusta números.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:opacity-90 disabled:opacity-50"
          data-testid="stock-audit-refresh-btn"
        >
          <ArrowsClockwise size={18} />
          {loading ? 'Cargando…' : 'Refrescar'}
        </button>
      </div>

      {/* Disclaimer educativo */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 flex gap-3" data-testid="stock-audit-disclaimer">
        <Info size={22} className="text-blue-600 flex-shrink-0 mt-0.5" weight="fill" />
        <div className="text-sm text-blue-900 leading-relaxed">
          <p className="font-semibold mb-1">Esta pantalla no arregla nada — solo te muestra dónde investigar.</p>
          <p className="text-blue-800">
            Si aparece un descuadre: (1) mira el <b>historial de movimientos</b> del producto para entender qué pasó,
            (2) si es un evento real (rotura, robo, mal conteo físico), regístralo en <b>Inventario → Ajuste</b> con motivo claro,
            (3) si no encuentras causa, es un bug del sistema — repórtalo.
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-3 text-sm mb-4" data-testid="stock-audit-error">
          {error}
        </div>
      )}

      {data && (
        <>
          {/* Resumen */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <p className="text-xs uppercase text-slate-500">Productos escaneados</p>
              <p className="text-2xl font-bold text-slate-800 mt-1" data-testid="stock-audit-scanned">{data.products_scanned}</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <p className="text-xs uppercase text-emerald-700">Slots OK</p>
              <p className="text-2xl font-bold text-emerald-800 mt-1" data-testid="stock-audit-ok">{data.slots_ok}</p>
              <p className="text-[10px] text-emerald-600 mt-1">producto × sucursal</p>
            </div>
            <div className={`${data.slots_mismatch > 0 ? 'bg-amber-50 border-amber-300' : 'bg-slate-50 border-slate-200'} border rounded-lg p-4`}>
              <p className="text-xs uppercase text-amber-700">Descuadres</p>
              <p className="text-2xl font-bold text-amber-800 mt-1" data-testid="stock-audit-mismatch">{data.slots_mismatch}</p>
              <p className="text-[10px] text-amber-600 mt-1">requiere investigación</p>
            </div>
          </div>

          {/* Tabla */}
          {data.mismatches.length === 0 ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-8 text-center" data-testid="stock-audit-empty">
              <CheckCircle size={48} weight="fill" className="mx-auto text-emerald-600 mb-3" />
              <p className="font-bold text-emerald-800 text-lg">✅ Todo cuadra</p>
              <p className="text-sm text-emerald-700 mt-1">No hay descuadres entre el stock actual y los movimientos registrados.</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="stock-audit-table">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-xs uppercase text-slate-600">
                  <tr>
                    <th className="text-left p-3">Producto</th>
                    <th className="text-left p-3">Sucursal</th>
                    <th className="text-right p-3">Actual</th>
                    <th className="text-right p-3">Movimientos</th>
                    <th className="text-right p-3">Compras</th>
                    <th className="text-right p-3">Diferencia</th>
                    <th className="text-center p-3">Investigar</th>
                  </tr>
                </thead>
                <tbody>
                  {data.mismatches.map((m) => {
                    const key = `${m.product_id}:${m.branch_id}`;
                    return (
                      <tr key={key} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`stock-audit-row-${key}`}>
                        <td className="p-3">
                          <p className="font-semibold text-slate-800">{m.name}</p>
                          {m.sku && <p className="text-xs text-slate-400 font-mono">{m.sku}</p>}
                        </td>
                        <td className="p-3">
                          <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                            m.branch_name === 'Morelos'
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-sky-100 text-sky-800'
                          }`}>
                            {m.branch_name}
                          </span>
                        </td>
                        <td className="p-3 text-right font-mono text-slate-700">{m.actual}</td>
                        <td className="p-3 text-right font-mono text-slate-500">{m.movements_total}</td>
                        <td className="p-3 text-right font-mono text-slate-500">{m.purchases_total}</td>
                        <td className="p-3 text-right">
                          <span className="inline-flex items-center gap-1 font-bold text-rose-700">
                            <Warning size={14} weight="bold" />
                            {m.diff}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <Link
                            to={`/inventory?product=${m.product_id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-white border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50"
                            data-testid={`stock-audit-investigate-${key}`}
                          >
                            <MagnifyingGlass size={12} />
                            Ver movimientos
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-[11px] text-slate-400 mt-4 italic">
            Actualizado {new Date(data.scanned_at).toLocaleString('es-MX')}
          </p>
        </>
      )}
    </div>
  );
}
